import type { CellHighlightProps } from './cell-highlight.types';

export interface OverlayPosition {
  key: string;
  left: number;
  top: number;
  width: number;
  height: number;
  className?: string;
}

type CellsByRowAndColumn = Map<string, Map<string, HTMLElement>>;

export const areOverlaysEqual = (a: OverlayPosition[], b: OverlayPosition[]): boolean => {
  if (a.length !== b.length) return false;

  for (let index = 0; index < a.length; index++) {
    const left = a[index];
    const right = b[index];
    if (
      left.key !== right.key ||
      left.left !== right.left ||
      left.top !== right.top ||
      left.width !== right.width ||
      left.height !== right.height ||
      left.className !== right.className
    ) {
      return false;
    }
  }

  return true;
};

export const buildCellsByRowAndColumn = (tableElement: HTMLTableElement): CellsByRowAndColumn => {
  const rowsByIdAndColumn: CellsByRowAndColumn = new Map();
  const rowElements = tableElement.querySelectorAll<HTMLElement>('tbody tr[data-row-id]');

  for (const rowElement of rowElements) {
    const rowId = rowElement.dataset.rowId;
    if (!rowId) continue;

    const cellsByColumn = new Map<string, HTMLElement>();
    const cellElements = rowElement.querySelectorAll<HTMLElement>('td[data-column-id]');
    for (const cellElement of cellElements) {
      const columnId = cellElement.dataset.columnId;
      if (!columnId) continue;
      cellsByColumn.set(columnId, cellElement);
    }

    rowsByIdAndColumn.set(rowId, cellsByColumn);
  }

  return rowsByIdAndColumn;
};

export const buildOverlayPositions = (
  tableElement: HTMLTableElement,
  targets: CellHighlightProps['targets'],
  className?: string
): OverlayPosition[] => {
  const tableRect = tableElement.getBoundingClientRect();
  const cellsByRowAndColumn = buildCellsByRowAndColumn(tableElement);
  const overlays: OverlayPosition[] = [];

  for (const { rowId, columnId, className: targetClassName } of targets) {
    const cellElement = cellsByRowAndColumn.get(rowId)?.get(columnId);
    if (!cellElement) continue;

    const cellRect = cellElement.getBoundingClientRect();
    overlays.push({
      key: `${rowId}:${columnId}`,
      left: cellRect.left - tableRect.left,
      top: cellRect.top - tableRect.top,
      width: cellRect.width,
      height: cellRect.height,
      className: targetClassName ?? className,
    });
  }

  return overlays;
};
